/**
 * `3d` — Primeiro acesso ao padrão (assistente de reaproveitamento). Por cliente.
 *
 * 🔒 **BR-25: a análise por IA só roda por clique** em "analisar documentos com
 * IA" — nunca num efeito de montagem. A tela lista as sugestões que JÁ existem
 * (`GET …/sugestoes`, sem custo de IA) e só.
 *
 * Assíncrono (contrato do Tiago, 19/10/2026): o clique dispara o job e a tela
 * espera o polling terminar — o botão fica em "analisando…" o tempo todo. Um
 * backend ainda sem a análise responde **501 `ia_indisponivel`**, e a tela diz
 * isso como informação, não como erro.
 *
 * Aceitar ACRESCENTA o requisito novo ao documento; o vínculo anterior permanece.
 * ⚠️ EM ABERTO (§9): faixas de confiança e se "aceitar todas" deveria se limitar à
 * alta. Por ora aceita todas as pendentes, como o contrato.
 */
import type { Sugestao } from '@/api/contracts/compliance'
import {
  useAceitarTodas,
  useAnalisarSugestoes,
  useDecidirSugestao,
  useEstrutura,
  useSugestoes,
} from '@/api/queries/compliance'
import { codigoDoErro, mensagemDoErro } from '@/components/erros'
import { EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Badge, Botao } from '@/components/primitivos'
import { usePermission } from '@/permissions/usePermission'
import { useClienteAtual } from '@/tenant/useClienteAtual'

import { usePadraoSelecionado } from '../padrao'

const CONFIANCA: Record<Sugestao['confianca'], { rotulo: string; tom: 'sucesso' | 'atencao' | 'neutro' }> = {
  alta: { rotulo: 'alta', tom: 'sucesso' },
  media: { rotulo: 'média', tom: 'atencao' },
  baixa: { rotulo: 'baixa', tom: 'neutro' },
}

/** Mesmo texto do Discovery-IA (`modules/finops/textos.ts`) — um módulo não importa de outro. */
function textoDoResultadoDaIa(quantidade: number): string {
  if (quantidade === 0) return 'A IA concluiu a análise, mas não encontrou nenhuma sugestão nova desta vez.'
  return quantidade === 1 ? '1 sugestão nova gerada pela IA.' : `${quantidade} sugestões novas geradas pela IA.`
}

const ICONE: Record<Sugestao['evidencia']['tipo'], string> = { pdf: 'file-pdf', docx: 'file-doc', link: 'link' }

export function NovoPadrao() {
  const idCliente = useClienteAtual()
  const { padrao, consulta: padroes } = usePadraoSelecionado()
  const { editar } = usePermission('compliance.questionario')
  const sugestoes = useSugestoes(idCliente, padrao?.id)
  const estrutura = useEstrutura(padrao?.id)

  if (padroes.isError) return <EstadoDeErro erro={padroes.error} aoTentarNovamente={() => void padroes.refetch()} />
  if (!padroes.isPending && !padrao) return <EstadoVazio titulo="Nenhum padrão de conformidade cadastrado." />
  if (!padrao || sugestoes.isPending) return <EsqueletoDeTabela rotulo="Carregando sugestões…" linhas={4} colunas={4} comBorda />
  if (sugestoes.isError) return <EstadoDeErro erro={sugestoes.error} aoTentarNovamente={() => void sugestoes.refetch()} />

  return (
    <Assistente
      key={`${idCliente}-${padrao.id}`}
      idCliente={idCliente}
      padraoId={padrao.id}
      padraoNome={padrao.nome}
      sugestoes={sugestoes.data}
      totalDePerguntas={estrutura.data?.perguntas.length}
      editar={editar}
    />
  )
}

function Assistente({
  idCliente,
  padraoId,
  padraoNome,
  sugestoes,
  totalDePerguntas,
  editar,
}: {
  idCliente: string
  padraoId: string
  padraoNome: string
  sugestoes: Sugestao[]
  totalDePerguntas: number | undefined
  editar: boolean
}) {
  const analisar = useAnalisarSugestoes(idCliente, padraoId)
  const aceitarTodas = useAceitarTodas(idCliente, padraoId)
  const decidir = useDecidirSugestao(idCliente)

  const visiveis = sugestoes.filter((s) => s.estado !== 'descartada')
  const pendentes = sugestoes.filter((s) => s.estado === 'pendente')
  const altas = pendentes.filter((s) => s.confianca === 'alta').length
  const cobertas = new Set(visiveis.map((s) => s.perguntaDestino.id)).size
  const iaIndisponivel = analisar.isError && codigoDoErro(analisar.error) === 'ia_indisponivel'

  return (
    <section className="flex flex-col gap-5">
      <div className="rounded-card border-navy-200 bg-navy-50 flex flex-wrap items-start gap-4 border p-5">
        <i className="ph ph-sparkle text-iox-teal text-3xl" aria-hidden="true" />
        <div className="flex min-w-60 flex-1 flex-col gap-1">
          <h2 className="type-h4 text-iox-navy">Reaproveitar evidências no padrão {padraoNome}</h2>
          <p className="type-body text-text-body">
            A IA compara os documentos já enviados com as perguntas do padrão {padraoNome}. Aceitar uma sugestão
            apenas <strong>acrescenta</strong> o novo requisito ao documento — o vínculo com o padrão anterior é
            mantido.
          </p>
        </div>
        {editar && (
          <div className="flex flex-wrap gap-2">
            <Botao icone="sparkle" disabled={analisar.isPending} onClick={() => analisar.mutate()}>
              {analisar.isPending ? 'analisando…' : 'analisar documentos com IA'}
            </Botao>
            <Botao
              variante="accent"
              disabled={pendentes.length === 0 || aceitarTodas.isPending}
              onClick={() => aceitarTodas.mutate()}
            >
              aceitar todas ({pendentes.length})
            </Botao>
          </div>
        )}
      </div>

      {iaIndisponivel && (
        <p role="status" className="rounded-card border-info-500 bg-info-100 text-info-700 flex gap-3 border p-4 type-body">
          <i className="ph ph-info mt-1" aria-hidden="true" />
          análise por IA ainda não disponível nesta versão
        </p>
      )}
      {analisar.data && (
        <p role="status" className="rounded-field bg-success-100 text-success-700 px-4 py-3 type-sm">
          {textoDoResultadoDaIa(analisar.data.quantidadeSugestoes)}
        </p>
      )}
      {analisar.isError && !iaIndisponivel && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(analisar.error)}
        </p>
      )}
      {(aceitarTodas.isError || decidir.isError) && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(aceitarTodas.error ?? decidir.error)}
        </p>
      )}

      {visiveis.length === 0 ? (
        <div className="border-border-subtle bg-surface-card rounded-card border">
          <EstadoVazio
            titulo="Nenhuma sugestão de reaproveitamento para este padrão."
            descricao="As sugestões aparecem aqui depois que a IA analisa os documentos já enviados."
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <span className="type-label text-text-body">
              {visiveis.length} {visiveis.length === 1 ? 'sugestão' : 'sugestões'} · {altas} de confiança alta
            </span>
            {totalDePerguntas !== undefined && totalDePerguntas > 0 && (
              <label className="flex min-w-72 flex-col gap-1">
                <span className="type-sm text-text-body">
                  perguntas do padrão {padraoNome} com evidência sugerida ({cobertas} / {totalDePerguntas})
                </span>
                <progress
                  className="h-2 w-full overflow-hidden rounded-control accent-iox-navy"
                  max={totalDePerguntas}
                  value={cobertas}
                />
              </label>
            )}
          </div>

          <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
            <table className="w-full text-left">
              <thead className="bg-subtle">
                <tr className="type-eyebrow text-text-muted">
                  <th className="px-4 py-3">Documento</th>
                  <th className="px-4 py-3">Pergunta sugerida ({padraoNome})</th>
                  <th className="px-4 py-3">Confiança</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((s) => {
                  const aceita = s.estado === 'aceita'
                  return (
                    <tr key={s.id} className={`border-border-subtle border-t ${aceita ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="text-text-body inline-flex items-center gap-2 type-body">
                          <i className={`ph ph-${ICONE[s.evidencia.tipo]} text-iox-navy`} aria-hidden="true" />
                          {s.evidencia.nome}
                        </span>
                      </td>
                      <td className="text-text-body px-4 py-3 type-body">
                        <strong>{s.perguntaDestino.codigo}</strong> {s.perguntaDestino.enunciado}
                        {s.vinculoAnterior && (
                          <span className="text-iox-teal">
                            {' '}
                            · antes {s.vinculoAnterior.codigo} ({s.vinculoAnterior.ano})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {aceita ? <Badge>incluída</Badge> : <Badge tom={CONFIANCA[s.confianca].tom}>{CONFIANCA[s.confianca].rotulo}</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        {aceita ? (
                          <span className="text-success-700 inline-flex items-center gap-1 type-label">
                            <i className="ph ph-check-circle" aria-hidden="true" /> vinculada
                          </span>
                        ) : (
                          editar && (
                            <span className="flex justify-end gap-2">
                              <Botao
                                aria-label={`descartar ${s.evidencia.nome} → ${s.perguntaDestino.codigo}`}
                                disabled={decidir.isPending}
                                onClick={() => decidir.mutate({ id: s.id, estado: 'descartada' })}
                              >
                                descartar
                              </Botao>
                              <Botao
                                variante="primaria"
                                aria-label={`incluir ${s.evidencia.nome} → ${s.perguntaDestino.codigo}`}
                                disabled={decidir.isPending}
                                onClick={() => decidir.mutate({ id: s.id, estado: 'aceita' })}
                              >
                                incluir
                              </Botao>
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="rounded-card border-border-subtle bg-subtle text-text-body flex gap-3 border p-4 type-sm">
        <i className="ph ph-info text-iox-navy mt-0.5" aria-hidden="true" />
        <span>
          O mesmo mecanismo que julga a aderência da evidência é usado aqui no sentido inverso: em vez de avaliar
          um documento contra uma pergunta, procura a pergunta que combina com cada documento. As sugestões pendentes
          também são avisadas no questionário.
        </span>
      </p>
    </section>
  )
}
